import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Bot } from '@/types/Bot';
import { supabase } from '@/integrations/supabase/client';

interface SearchSuggestion {
  tools: Array<{ id: string; name: string; use_case: string; thumbnail: string | null }>;
  categories: Array<{ id: string; name: string; emoji: string }>;
  popularSearches: string[];
  aiSuggestions: string[];
}

interface SearchResult {
  results: Bot[];
  query: string;
  expandedTerms: string[];
  totalResults: number;
  suggestions: string[];
}

interface SearchContextType {
  query: string;
  setQuery: (query: string) => void;
  suggestions: SearchSuggestion;
  searchResults: SearchResult | null;
  isSearching: boolean;
  isFetchingSuggestions: boolean;
  performSearch: (searchQuery: string, category?: string) => Promise<void>;
  fetchSuggestions: (query: string) => Promise<void>;
  clearSearch: () => void;
  highlightText: (text: string, query: string) => React.ReactNode;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion>({
    tools: [],
    categories: [],
    popularSearches: [],
    aiSuggestions: []
  });
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions({ tools: [], categories: [], popularSearches: [], aiSuggestions: [] });
      return;
    }

    setIsFetchingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-suggestions', {
        body: { query: searchQuery }
      });

      if (error) throw error;
      setSuggestions(data || { tools: [], categories: [], popularSearches: [], aiSuggestions: [] });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      // Fallback to basic suggestions
      const { data: bots } = await supabase
        .from('bots')
        .select('id, name, use_case, thumbnail')
        .or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
        .limit(5);
      
      setSuggestions({
        tools: bots || [],
        categories: [],
        popularSearches: [],
        aiSuggestions: [`AI for ${searchQuery}`, `Best ${searchQuery} tools`]
      });
    } finally {
      setIsFetchingSuggestions(false);
    }
  }, []);

  const performSearch = useCallback(async (searchQuery: string, category?: string) => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setQuery(searchQuery);

    try {
      const { data, error } = await supabase.functions.invoke('smart-search', {
        body: { query: searchQuery, category, limit: 50 }
      });

      if (error) throw error;

      // Map the results to Bot type
      const mappedResults: Bot[] = (data.results || []).map((b: any) => ({
        id: b.id,
        name: b.name,
        description: b.description || '',
        category: b.use_case || 'General',
        rating: Number(b.rating ?? 4.5),
        reviewCount: Number(b.conversations ?? 0),
        price: b.pricing || 'Free',
        features: (b.key_features || b.features_array || []) as string[],
        tags: (b.tags || []) as string[],
        popularity: Number(b.fit_index ?? 50),
        imageUrl: b.thumbnail || '/api/placeholder/400/300',
        website: b.website_url || b.site_url || '',
        modelType: b.model_type || undefined,
        loginRequired: Boolean(b.login_required),
        preview_url: b.preview_url ?? null,
        site_url: b.site_url ?? null,
        thumbnail: b.thumbnail ?? null,
        key_features: (b.key_features || []) as string[],
        subcategory: b.subcategory || undefined,
        best_use_cases: (b.best_use_cases || []) as string[],
        fit_index: Number(b.fit_index ?? 50),
        relevanceScore: b.relevance_score
      }));

      setSearchResults({
        results: mappedResults,
        query: searchQuery,
        expandedTerms: data.expandedTerms || [],
        totalResults: data.totalResults || 0,
        suggestions: data.suggestions || []
      });
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to basic search
      const { data: bots } = await supabase
        .from('bots')
        .select('*')
        .or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,use_case.ilike.%${searchQuery}%`)
        .limit(50);

      const mapped: Bot[] = (bots || []).map((b: any) => ({
        id: b.id,
        name: b.name,
        description: b.description || '',
        category: b.use_case || 'General',
        rating: Number(b.rating ?? 4.5),
        reviewCount: Number(b.conversations ?? 0),
        price: b.pricing || 'Free',
        features: (b.key_features || []) as string[],
        tags: (b.tags || []) as string[],
        popularity: Number(b.fit_index ?? 50),
        imageUrl: b.thumbnail || '/api/placeholder/400/300',
        website: b.website_url || b.site_url || '',
        key_features: (b.key_features || []) as string[],
        fit_index: Number(b.fit_index ?? 50),
      }));

      setSearchResults({
        results: mapped,
        query: searchQuery,
        expandedTerms: [searchQuery],
        totalResults: mapped.length,
        suggestions: []
      });
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setSearchResults(null);
    setSuggestions({ tools: [], categories: [], popularSearches: [], aiSuggestions: [] });
  }, []);

  const highlightText = useCallback((text: string, searchQuery: string): React.ReactNode => {
    if (!searchQuery.trim() || !text) return text;

    const terms = searchQuery.toLowerCase().split(/\s+/).filter(t => t.length > 2);
    if (terms.length === 0) return text;

    const regex = new RegExp(`(${terms.join('|')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      const isMatch = terms.some(term => part.toLowerCase() === term);
      if (isMatch) {
        return (
          <mark key={index} className="bg-primary/20 text-primary rounded px-0.5">
            {part}
          </mark>
        );
      }
      return part;
    });
  }, []);

  return (
    <SearchContext.Provider value={{
      query,
      setQuery,
      suggestions,
      searchResults,
      isSearching,
      isFetchingSuggestions,
      performSearch,
      fetchSuggestions,
      clearSearch,
      highlightText
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};
